import { BROADCAST_CHANNELS } from '~/constants/broadcastChannels';

type OfflineFavoriteMessage = { count: number };

let channel: BroadcastChannel | null = null;
let sessionCount = 0;

function getChannel(): BroadcastChannel | null {
	if (import.meta.server) return null;
	if (!channel) {
		try {
			channel = new BroadcastChannel(BROADCAST_CHANNELS.OFFLINE_FAVORITES_SYNC);
		} catch {
			return null;
		}
	}
	return channel;
}

export function useOfflineFavoriteCountSync() {
	function initListener(onCount: (count: number) => void): () => void {
		const ch = getChannel();
		if (!ch) return () => {};

		const handler = (event: MessageEvent<OfflineFavoriteMessage>) => {
			sessionCount = event.data.count;
			onCount(sessionCount);
		};

		ch.addEventListener('message', handler);
		return () => ch.removeEventListener('message', handler);
	}

	function broadcastCount(count: number): void {
		sessionCount = count;
		getChannel()?.postMessage({ count } satisfies OfflineFavoriteMessage);
	}

	function getSessionCount(): number {
		return sessionCount;
	}

	function incrementSessionCount(): number {
		sessionCount++;
		broadcastCount(sessionCount);
		return sessionCount;
	}

	function decrementSessionCount(): number {
		sessionCount = Math.max(0, sessionCount - 1);
		broadcastCount(sessionCount);
		return sessionCount;
	}

	function resetSessionCount(): void {
		sessionCount = 0;
		broadcastCount(0);
	}

	return {
		initListener,
		getSessionCount,
		incrementSessionCount,
		decrementSessionCount,
		resetSessionCount
	};
}
